import { SsqSsqHeading } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { ScoreChart } from "src/components/ScoreChart/ScoreChart";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useLocation } from "react-router";
import { useEffect } from "react";
import { useEntityScoresOrUnavailable } from "src/context/Scores/useEntityScores";
import { Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";
import { NoCompanyPost } from "./NoCompanyPost";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { QuestionnairesList } from "src/components/QuestionnairesList/QuestionnairesList";
import { getSameLevelDimensionsChartData } from "src/utils/questionnaire";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

export const Questionnaire = () => {
    const { company } = useCompanyMetadata();
    const {
        product,
        ssqVersion,
        productPosts,
        activeCompanyId,
        activePost,
        isActivePostRefetching,
        isActivePostLoading,
        canModifySSQ,
    } = useProductMetadataContext();

    const entityScores = useEntityScoresOrUnavailable();
    const { setEntitySsq } = entityScores;
    const { selectedCompany } = useFoodpilotContext();
    const location = useLocation();
    const theme = useTheme();
    const { t } = useTranslation();
    const isProductUxV2 = useIsProductUxV2();

    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");

    const isIntermediateProduct = product.isIntermediate === true;
    const urlHandlerRoutes = isIntermediateProduct ? URLHandler.intermediateProduct : URLHandler.product;

    useEffect(() => {
        setEntitySsq(ssqVersion?.ssq);
    }, [ssqVersion]);

    if (selectedCompany === null) return <Loading />;

    const scoresSection = () => {
        if (!entityScores.isAvailable) return null;

        const { scoresWithVariations, lastSelectedVariation } = entityScores;
        const { data, totalScore, maxScore, specificityRate } = getSameLevelDimensionsChartData(
            activePost?.olapScores[lastSelectedVariation.scoreId],
            ["headings", "ssq_versions"],
            { ssqVersion },
        );

        return (
            <ScoreChart
                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                scoresWithVariations={scoresWithVariations}
                selectedVariation={lastSelectedVariation}
                totalScore={totalScore}
                data={data || undefined}
                isLoading={isActivePostRefetching}
                maxScore={maxScore}
                specificityRate={activePost?.isLocked ? specificityRate : undefined}
            />
        );
    };

    return (
        <Stack gap={theme.spacing(2)}>
            {(
                productPosts !== undefined &&
                isActivePostLoading === false &&
                activePost === undefined &&
                canModifySSQ === false
            ) ?
                <NoCompanyPost />
            :   <QuestionnairesList
                    ssqVersion={ssqVersion}
                    exportSubject={{ type: "product", id: product.id, name: product.title }}
                    post={activePost}
                    isSsqLoading={false}
                    isPostLoading={false}
                    displayDateLastModified={false}
                    completeQuestionnaireUrl={(ssqSsqHeading: SsqSsqHeading) => {
                        const url = urlHandlerRoutes.ssqHeading(
                            company.id,
                            product.id,
                            activeCompanyId ?? 0,
                            ssqSsqHeading.id,
                        );
                        return returnUrl ? `${url}?returnUrl=${encodeURIComponent(returnUrl)}` : url;
                    }}
                    scoresSection={scoresSection()}
                    enableTableHead={false}
                    muiTableProps={{
                        sx: {
                            border: "none",
                        },
                    }}
                    title={t("questionnaire_du_produit")}
                />
            }
        </Stack>
    );
};
